import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProfile } from '../hooks/useProfile';
import { ProfileUpdateData, PasswordUpdateData } from '../services/profileService';
import LocationSelector, { LocationData } from '../components/LocationSelector';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Input, Select } from '../components/ui/Input';
import { Avatar } from '../components/ui/Avatar';
import { Callout } from '../components/ui/Callout';
import { Badge } from '../components/ui/Badge';
import { Divider } from '../components/ui/Divider';
import { Icon } from '../components/ui/Icon';

type Tab = 'profile' | 'security' | 'preferences';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { translate, isRTL } = useLanguage();
  const { updateProfile, updatePassword, isUpdating, isUpdatingPassword } = useProfile();

  const [tab, setTab] = useState<Tab>('profile');
  const [success, setSuccess] = useState<string | null>(null);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const [form, setForm] = useState<ProfileUpdateData>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    use_for: user?.use_for || 'personal',
    language: user?.language || 'en',
    company: user?.company || '',
    role: user?.role || '',
    country: user?.country?.name || '',
    state: user?.state?.name || '',
    city: user?.city?.name || '',
    street: user?.street || '',
    house_no: user?.house_no || '',
    zip_code: user?.zip_code || '',
    country_id: user?.country_id || null,
    state_id: user?.state_id || null,
    city_id: user?.city_id || null,
    city_other: user?.city_other || '',
  });

  const [pwdForm, setPwdForm] = useState<PasswordUpdateData>({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        use_for: user.use_for || 'personal',
        language: user.language || 'en',
        company: user.company || '',
        role: user.role || '',
        country: user.country?.name || '',
        state: user.state?.name || '',
        city: user.city?.name || '',
        street: user.street || '',
        house_no: user.house_no || '',
        zip_code: user.zip_code || '',
        country_id: user.country_id || null,
        state_id: user.state_id || null,
        city_id: user.city_id || null,
        city_other: user.city_other || '',
      });
    }
  }, [user]);

  const handleField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handlePwdField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPwdForm((p) => ({ ...p, [name]: value }));
  };

  const handleLocationChange = (loc: LocationData) => {
    setForm((p) => ({
      ...p,
      country_id: loc.country_id,
      state_id: loc.state_id,
      city_id: loc.city_id,
      city_other: loc.city_other,
      country: loc.country || p.country,
      state: loc.state || p.state,
      city: loc.city || p.city,
    }));
  };

  const handleSaveInfo = async () => {
    try {
      const result = await updateProfile(form);
      setSuccess(result.message);
      setTimeout(() => setSuccess(null), 3500);
    } catch {
      /* useProfile sets error state */
    }
  };

  const handleSavePassword = async () => {
    try {
      const result = await updatePassword(pwdForm);
      setSuccess(result.message);
      setPwdForm({ current_password: '', password: '', password_confirmation: '' });
      setTimeout(() => setSuccess(null), 3500);
    } catch {
      /* useProfile sets error state */
    }
  };

  const initials = ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase() || 'U';
  const pwdMismatch =
    pwdForm.password !== pwdForm.password_confirmation && pwdForm.password_confirmation !== '';

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>{translate('profile') || 'Account'}</PageTitle>
          <PageSub>Manage your personal information, contact details, and account settings.</PageSub>
        </div>
      </PageHead>

      {success && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">
            <Icon name="checkCircle" size={16} />
            <span>{success}</span>
          </Callout>
        </div>
      )}

      <div className="tabs profile-tabs" style={{ marginBottom: 24 }}>
        {(['profile', 'security', 'preferences'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }} className="details-grid">
          <Card pad style={{ padding: 28 }} className="profile-card">
            <h3 style={{ fontSize: 16, marginBottom: 16 }}>Personal information</h3>
            <div
              className="profile-identity"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                background: 'var(--bg-tint)',
                borderRadius: 10,
                marginBottom: 20,
              }}
            >
              <Avatar size={56}>{initials}</Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.first_name || ''} {user?.last_name || ''}
                </div>
                <div className="muted" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.email}
                </div>
              </div>
            </div>
            <div className="profile-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label={translate('first-name') || 'First name'}>
                <Input name="first_name" value={form.first_name} onChange={handleField} />
              </Field>
              <Field label={translate('last-name') || 'Last name'}>
                <Input name="last_name" value={form.last_name} onChange={handleField} />
              </Field>
              <Field label={translate('email-address') || 'Email'} className="profile-span-2" style={{ gridColumn: 'span 2' }}>
                <Input name="email" type="email" value={form.email} onChange={handleField} />
              </Field>
              <Field label={translate('phone-number') || 'Phone'}>
                <Input name="phone" value={form.phone} onChange={handleField} />
              </Field>
              <Field label={translate('use-for') || 'Use for'}>
                <Select name="use_for" value={form.use_for} onChange={handleField}>
                  <option value="personal">{translate('personal') || 'Personal'}</option>
                  <option value="business">{translate('business') || 'Business'}</option>
                  <option value="other">{translate('other') || 'Other'}</option>
                </Select>
              </Field>
              <Field label={translate('company') || 'Company'}>
                <Input name="company" value={form.company} onChange={handleField} />
              </Field>
              <Field label={translate('role') || 'Role'}>
                <Input name="role" value={form.role} onChange={handleField} />
              </Field>
            </div>

            <Divider style={{ margin: '24px 0' }} />

            <h3 style={{ fontSize: 16, marginBottom: 16 }}>
              {translate('address-information') || 'Address'}
            </h3>
            <LocationSelector
              value={{
                country_id: form.country_id || null,
                state_id: form.state_id || null,
                city_id: form.city_id || null,
                city_other: form.city_other || '',
                country: form.country,
                state: form.state,
                city: form.city,
              }}
              onChange={handleLocationChange}
              isRTL={isRTL}
            />
            <div className="profile-grid-3" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginTop: 12 }}>
              <Field label={translate('street') || 'Street'}>
                <Input name="street" value={form.street} onChange={handleField} />
              </Field>
              <Field label={translate('house-no') || 'House #'}>
                <Input name="house_no" value={form.house_no} onChange={handleField} />
              </Field>
              <Field label={translate('zip-code') || 'ZIP'}>
                <Input name="zip_code" value={form.zip_code} onChange={handleField} />
              </Field>
            </div>

            <div className="profile-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <Button variant="primary" disabled={isUpdating} onClick={handleSaveInfo}>
                <Icon name="check" size={14} />
                <span>{isUpdating ? 'Saving…' : translate('save-changes') || 'Save changes'}</span>
              </Button>
            </div>
          </Card>

          <Card pad>
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Verification</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Email', verified: !!user?.email_verified_at },
                { label: 'Phone', verified: !!(user as any)?.phone_verified_at },
                { label: 'Business docs', verified: false, pending: true },
              ].map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 10,
                    borderRadius: 8,
                    background: 'var(--bg-tint)',
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{row.label}</span>
                  {row.verified ? (
                    <Badge tone="ok">Verified</Badge>
                  ) : row.pending ? (
                    <Badge tone="warn">Pending</Badge>
                  ) : (
                    <Badge>Unverified</Badge>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === 'security' && (
        <Card pad style={{ maxWidth: 720, padding: 28 }} className="profile-card">
          <h3 style={{ fontSize: 16 }}>{translate('change-password') || 'Change password'}</h3>
          <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
            Choose a strong password with at least 8 characters, including uppercase, lowercase,
            numbers, and special characters.
          </p>

          <div style={{ marginTop: 16 }}>
            <Field label={translate('current-password') || 'Current password'}>
              <div className="profile-pwd-wrap" style={{ position: 'relative' }}>
                <Input
                  name="current_password"
                  type={showCurrentPwd ? 'text' : 'password'}
                  value={pwdForm.current_password}
                  onChange={handlePwdField}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-xs profile-pwd-toggle"
                  style={{ position: 'absolute', insetInlineEnd: 4, top: 4 }}
                  onClick={() => setShowCurrentPwd((v) => !v)}
                  aria-label="Toggle password visibility"
                >
                  <Icon name="eye" size={13} />
                </button>
              </div>
            </Field>
            <div className="profile-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
              <Field label={translate('new-password') || 'New password'}>
                <div className="profile-pwd-wrap" style={{ position: 'relative' }}>
                  <Input
                    name="password"
                    type={showNewPwd ? 'text' : 'password'}
                    value={pwdForm.password}
                    onChange={handlePwdField}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs profile-pwd-toggle"
                    style={{ position: 'absolute', insetInlineEnd: 4, top: 4 }}
                    onClick={() => setShowNewPwd((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    <Icon name="eye" size={13} />
                  </button>
                </div>
              </Field>
              <Field
                label={translate('confirm-password') || 'Confirm password'}
                hint={pwdMismatch ? 'Passwords do not match' : undefined}
              >
                <div className="profile-pwd-wrap" style={{ position: 'relative' }}>
                  <Input
                    name="password_confirmation"
                    type={showConfirmPwd ? 'text' : 'password'}
                    value={pwdForm.password_confirmation}
                    onChange={handlePwdField}
                    style={pwdMismatch ? { borderColor: 'var(--err)' } : undefined}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs profile-pwd-toggle"
                    style={{ position: 'absolute', insetInlineEnd: 4, top: 4 }}
                    onClick={() => setShowConfirmPwd((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    <Icon name="eye" size={13} />
                  </button>
                </div>
              </Field>
            </div>
          </div>

          <div className="profile-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <Button
              variant="primary"
              disabled={
                isUpdatingPassword ||
                pwdMismatch ||
                !pwdForm.current_password ||
                !pwdForm.password
              }
              onClick={handleSavePassword}
            >
              <Icon name="lock" size={14} />
              <span>
                {isUpdatingPassword ? 'Updating…' : translate('save-password') || 'Save password'}
              </span>
            </Button>
          </div>
        </Card>
      )}

      {tab === 'preferences' && (
        <Card pad style={{ maxWidth: 720, padding: 28 }} className="profile-card">
          <h3 style={{ fontSize: 16, marginBottom: 14 }}>Language & region</h3>
          <div className="profile-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={translate('preferred-language') || 'Language'}>
              <Select name="language" value={form.language} onChange={handleField}>
                <option value="english">English</option>
                <option value="hebrew">עברית</option>
                <option value="arabic">العربية</option>
                <option value="russian">Русский</option>
              </Select>
            </Field>
          </div>
          <div className="profile-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <Button variant="primary" disabled={isUpdating} onClick={handleSaveInfo}>
              <Icon name="check" size={14} />
              <span>{isUpdating ? 'Saving…' : 'Save preferences'}</span>
            </Button>
          </div>
        </Card>
      )}
    </Page>
  );
};

export default ProfilePage;
